import { useRef, useState } from 'react';
import { uploadTemplateImage, useConfig } from '../api/queries';
import { errorMessage } from '../lib/api';
import { fileUrl } from '../lib/utils';
import { useToast } from './Toast';

const ACCEPT = 'image/png,image/jpeg,image/webp,image/gif';

interface Props {
  value?: string;
  onChange: (imageId: string | undefined) => void;
  label?: string;
  compact?: boolean;
}

/** Uploads an image to the server and stores only its id in the template. */
export function ImageUploader({ value, onChange, label = 'Image', compact }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const maxMb = useConfig().data?.maxUploadMb ?? 8;

  async function handleFile(file: File | undefined) {
    if (!file) return;
    if (file.size > maxMb * 1024 * 1024) {
      toast.error(`Image is larger than ${maxMb} MB`);
      return;
    }
    setBusy(true);
    try {
      const uploaded = await uploadTemplateImage(file);
      onChange(uploaded.id);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  return (
    <div className={compact ? 'image-uploader compact' : 'image-uploader'}>
      {value ? (
        <img src={fileUrl(value)} alt="" className="image-uploader-preview" />
      ) : (
        !compact && <div className="image-uploader-empty">No {label.toLowerCase()}</div>
      )}
      <div className="row">
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
        >
          {busy
            ? 'Uploading…'
            : value
              ? `Replace ${label.toLowerCase()}`
              : `Add ${label.toLowerCase()}`}
        </button>
        {value && (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => onChange(undefined)}
          >
            Remove
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        hidden
        onChange={(e) => void handleFile(e.target.files?.[0])}
      />
    </div>
  );
}
